import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import moment from 'moment';
import { calculateWithUnit } from '../../utils/balanceGroupStatsHelpers';
import { formatNumber } from '../../utils/i18n';
import { UNITS_EXTENDED } from '../../constants/units';

const MARGIN = 30;
const COLOR_STROKE = '#E5E7EB';
const COLOR_ALLOCATED = '#9B6EB5';
const COLOR_SUBSCRIPTION = '#4B5563';
const COLOR_PEAK = '#732597';
const COLOR_TEXT = '#374151';
const BAR_WIDTH = 10;
const LINE_WIDTH = 2;
const SUBSCRIPTION_LINE_WIDTH = 1.5;

const CapacitySubscriptionChargeChart = ({ data, selectedUnit }) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return null;
  }

  const unitLabel = selectedUnit === UNITS_EXTENDED.MWH ? t('common.label.mwhPerHour') : t('common.label.kwhPerHour');

  const formatValue = (val) => {
    if (val == null) return '-';
    const converted = calculateWithUnit(selectedUnit, val);
    if (!converted) return '-';
    return `${formatNumber(converted.toNumber(), 0, 3)} ${unitLabel}`;
  };

  const ticks = data.length > 1
    ? [data[0].timestamp, data[data.length - 1].timestamp]
    : data.map((d) => d.timestamp);

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart
        data={data}
        margin={{
          top: MARGIN,
          right: MARGIN,
          bottom: MARGIN,
          left: MARGIN,
        }}
      >
        <CartesianGrid stroke={COLOR_STROKE} />
        <XAxis
          dataKey="timestamp"
          ticks={ticks}
          tickFormatter={(time) => moment(time).format('YYYY-MM-DD HH:mm')}
          tick={{ fill: COLOR_TEXT }}
          tickLine={{ stroke: COLOR_TEXT }}
          axisLine={{ stroke: COLOR_TEXT }}
        />
        <YAxis
          tickFormatter={(val) => calculateWithUnit(selectedUnit, val).toNumber()}
          tick={{ fill: COLOR_TEXT }}
          tickLine={{ stroke: COLOR_TEXT }}
          axisLine={{ stroke: COLOR_TEXT }}
        />
        <Tooltip
          labelFormatter={(time) => moment(time).format('YYYY-MM-DD HH:mm')}
          formatter={(value, name) => [formatValue(value), name]}
          contentStyle={{ borderColor: COLOR_STROKE }}
          labelStyle={{ color: COLOR_TEXT }}
        />
        <Legend
          verticalAlign="bottom"
          height={36}
          wrapperStyle={{ color: COLOR_TEXT }}
          iconType="plainline"
        />
        <Bar
          dataKey="allocatedEnergy"
          name={t('capacitySubscriptionCharge.table.allocatedEnergy')}
          fill={COLOR_ALLOCATED}
          fillOpacity={0.9}
          barSize={BAR_WIDTH}
        />
        <Line
          type="stepAfter"
          dataKey="capacitySubscription"
          name={t('capacitySubscriptionCharge.table.capacitySubscription')}
          stroke={COLOR_SUBSCRIPTION}
          strokeWidth={SUBSCRIPTION_LINE_WIDTH}
          strokeDasharray="5 5"
          dot={false}
        />
        <Line
          type="monotone"
          dataKey="tehohuippu"
          name={t('capacitySubscriptionCharge.title')}
          stroke={COLOR_PEAK}
          strokeWidth={LINE_WIDTH}
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

CapacitySubscriptionChargeChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    timestamp: PropTypes.number.isRequired,
    allocatedEnergy: PropTypes.number.isRequired,
    capacitySubscription: PropTypes.number.isRequired,
    tehohuippu: PropTypes.number,
  })).isRequired,
  selectedUnit: PropTypes.string.isRequired,
};

export default CapacitySubscriptionChargeChart;
